import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/ui/page-header";
import { LinkedInProfileService, LinkedInProfileData } from "@/services/linkedinProfileService";
import { 
  ArrowLeft, Copy, Download, Edit2, Save, X, FileText, User, 
  Briefcase, Award, Sparkles, RefreshCw, Loader2 
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export default function LinkedInProfileDetail() {
  const { profileId } = useParams<{ profileId: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<LinkedInProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedContent, setEditedContent] = useState<string>("");
  const [editedName, setEditedName] = useState<string>("");

  useEffect(() => {
    if (profileId) {
      loadProfile();
    }
  }, [profileId]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await LinkedInProfileService.loadProfile(profileId!);
      if (data) {
        setProfile(data);
        // Set the generated content for editing
        const rawContent = data.generated_profile?.rawContent || 
                          data.generated_report_data?.rawContent || 
                          data.raw_content_data?.rawContent ||
                          "";
        setEditedContent(rawContent);
        setEditedName(data.profile_name || "");
      } else {
        toast.error("Profile not found");
        navigate("/linkedin-profiles");
      }
    } catch (error) {
      console.error("Error loading profile:", error);
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!profile?.id) return;
    
    try {
      setIsSaving(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("You must be logged in");
        return;
      }

      // Update the profile with edited content
      const updatedGeneratedProfile = {
        ...profile.generated_profile,
        rawContent: editedContent
      };

      const { error } = await supabase
        .from('linkedin_profiles')
        .update({
          profile_name: editedName,
          generated_profile: updatedGeneratedProfile,
          updated_at: new Date().toISOString()
        })
        .eq('id', profile.id)
        .eq('user_id', session.user.id);

      if (error) throw error;

      // Update local state
      setProfile({
        ...profile,
        profile_name: editedName,
        generated_profile: updatedGeneratedProfile
      });

      setIsEditing(false);
      toast.success("Profile saved successfully");
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error("Failed to save profile");
    } finally {
      setIsSaving(false);
    }
  };

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${type} copied to clipboard`);
    } catch (err) {
      toast.error("Failed to copy. Please select and copy manually.");
    }
  };

  const downloadAsText = () => {
    const content = editedContent || "";
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${profile?.profile_name || "LinkedIn Profile"}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Downloaded as text file");
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "draft":
        return <Badge variant="outline">Draft</Badge>;
      case "content_saved":
        return <Badge className="bg-purple-100 text-purple-800">Content Saved</Badge>;
      case "generated":
        return <Badge className="bg-emerald-100 text-emerald-800">Completed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <PageHeader title="LinkedIn Profile" backTo="/linkedin-profiles" />
        <div className="flex items-center justify-center p-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background">
        <PageHeader title="LinkedIn Profile" backTo="/linkedin-profiles" />
        <div className="p-6 text-center">
          <p className="text-muted-foreground">Profile not found</p>
          <Button onClick={() => navigate("/linkedin-profiles")} className="mt-4">
            Back to Library
          </Button>
        </div>
      </div>
    );
  }

  const rawContent = profile.generated_profile?.rawContent || 
                     profile.generated_report_data?.rawContent || 
                     profile.raw_content_data?.rawContent ||
                     "";

  const hasGeneratedContent = profile.status === "generated" && rawContent;
  const hasDraftContent = profile.status === "draft" || profile.status === "content_saved";

  return (
    <div className="min-h-screen bg-background">
      <PageHeader 
        title={isEditing ? "Edit Profile" : "View Profile"} 
        backTo="/linkedin-profiles"
      >
        <div className="flex items-center gap-2">
          {hasGeneratedContent && !isEditing && (
            <>
              <Button variant="outline" size="sm" onClick={() => copyToClipboard(rawContent, "Full content")}>
                <Copy className="h-4 w-4 mr-1" />
                Copy All
              </Button>
              <Button variant="outline" size="sm" onClick={downloadAsText}>
                <Download className="h-4 w-4 mr-1" />
                Download
              </Button>
              <Button size="sm" onClick={() => setIsEditing(true)}>
                <Edit2 className="h-4 w-4 mr-1" />
                Edit
              </Button>
            </>
          )}
          {isEditing && (
            <>
              <Button variant="outline" size="sm" onClick={() => setIsEditing(false)} disabled={isSaving}>
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={isSaving}>
                {isSaving ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                Save Changes
              </Button>
            </>
          )}
          {hasDraftContent && (
            <Button onClick={() => navigate(`/linkedin-wizard?profileId=${profile.id}`)}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Continue Wizard
            </Button>
          )}
        </div>
      </PageHeader>

      <div className="p-6 max-w-5xl mx-auto space-y-6">
        {/* Profile Header Card */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                {isEditing ? (
                  <div className="space-y-2">
                    <Label htmlFor="profile-name">Profile Name</Label>
                    <Input
                      id="profile-name"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      className="max-w-md"
                    />
                  </div>
                ) : (
                  <CardTitle className="text-xl flex items-center gap-2">
                    <User className="h-5 w-5" />
                    {profile.profile_name || "Untitled Profile"}
                  </CardTitle>
                )}
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  {getStatusBadge(profile.status || "draft")}
                  {profile.target_job_titles && (
                    <span className="flex items-center gap-1">
                      <Briefcase className="h-3 w-3" />
                      {profile.target_job_titles}
                    </span>
                  )}
                </div>
              </div>
              {profile.status === "generated" && (
                <div className="flex items-center gap-1 text-green-600">
                  <Sparkles className="h-5 w-5" />
                  <span className="font-medium">AI Generated</span>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              {profile.industries && profile.industries.length > 0 && (
                <div>
                  <p className="text-muted-foreground">Industries</p>
                  <p className="font-medium">{profile.industries.join(", ")}</p>
                </div>
              )}
              {profile.voice_style && (
                <div>
                  <p className="text-muted-foreground">Voice Style</p>
                  <p className="font-medium capitalize">{profile.voice_style}</p>
                </div>
              )}
              <div>
                <p className="text-muted-foreground">Created</p>
                <p className="font-medium">{formatDate(profile.created_at || "")}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Last Updated</p>
                <p className="font-medium">{formatDate(profile.updated_at || profile.created_at || "")}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Content Section */}
        {hasGeneratedContent ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Generated LinkedIn Profile Content
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <Textarea
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  className="min-h-[600px] font-mono text-sm"
                  placeholder="Edit your LinkedIn profile content..."
                />
              ) : (
                <ScrollArea className="h-[600px]">
                  <div className="whitespace-pre-wrap font-mono text-sm p-4 bg-muted/30 rounded-lg">
                    {rawContent}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        ) : hasDraftContent ? (
          <Card>
            <CardContent className="py-12 text-center">
              <div className="flex flex-col items-center gap-4">
                <FileText className="h-12 w-12 text-muted-foreground" />
                <div>
                  <h3 className="font-semibold text-lg">Profile Not Yet Generated</h3>
                  <p className="text-muted-foreground mt-1">
                    This profile is still in progress. Continue the wizard to generate your optimized LinkedIn content.
                  </p>
                </div>
                <Button onClick={() => navigate(`/linkedin-wizard?profileId=${profile.id}`)}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Continue Wizard
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No content available for this profile.</p>
            </CardContent>
          </Card>
        )}

        {/* Wizard Data Summary (if available) */}
        {(profile.primary_goals?.length > 0 || profile.unique_selling_points?.length > 0) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5" />
                Profile Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="goals">
                <TabsList>
                  {profile.primary_goals?.length > 0 && (
                    <TabsTrigger value="goals">Goals</TabsTrigger>
                  )}
                  {profile.unique_selling_points?.length > 0 && (
                    <TabsTrigger value="usps">Unique Selling Points</TabsTrigger>
                  )}
                  {profile.skills && (
                    <TabsTrigger value="skills">Skills</TabsTrigger>
                  )}
                </TabsList>
                
                {profile.primary_goals?.length > 0 && (
                  <TabsContent value="goals">
                    <div className="flex flex-wrap gap-2 mt-4">
                      {profile.primary_goals.map((goal, idx) => (
                        <Badge key={idx} variant="secondary">{goal}</Badge>
                      ))}
                    </div>
                  </TabsContent>
                )}
                
                {profile.unique_selling_points?.length > 0 && (
                  <TabsContent value="usps">
                    <div className="flex flex-wrap gap-2 mt-4">
                      {profile.unique_selling_points.map((usp, idx) => (
                        <Badge key={idx} variant="outline">{usp}</Badge>
                      ))}
                    </div>
                  </TabsContent>
                )}
                
                {profile.skills && (
                  <TabsContent value="skills">
                    <p className="mt-4 text-sm">{profile.skills}</p>
                  </TabsContent>
                )}
              </Tabs>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
